import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { CacheService } from '@ukam/core/services/cache.service';
import { CredentialService } from '@ukam/core/services/credential.service';
import { API_BASE_URL } from '@ukam/core/tokens/api-base-url.token';

export interface SignInRequest {
  accessId: string;
  password: string;
}

export interface SignInResponse {
  user: string;
  token: string;
  expires: string;
  changePassword?: boolean;
}

export const PASSWORD_CHANGE_REQUIRED_CODE = '1015030';

export type ForgotPasswordType = 'email' | 'access_id';

export interface AuthCodeTextResult {
  code: string;
  text: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly supportCacheResetService = inject(CacheService);
  private readonly credentialService = inject(CredentialService);

  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly signInEndpoint = `${this.apiBaseUrl}/auth/sign-in`;
  private readonly forgotPasswordEndpoint = `${this.apiBaseUrl}/auth/forgot-password`;
  private readonly resetPasswordEndpoint = `${this.apiBaseUrl}/auth/reset-password`;
  private readonly resetCurrentPasswordEndpoint = `${this.apiBaseUrl}/auth/reset-current-password`;
  private readonly storageKey = 'ukam.auth.session';

  signIn(payload: SignInRequest, rememberSession = true): Observable<SignInResponse> {
    return this.http.post<SignInResponse>(this.signInEndpoint, payload).pipe(
      tap((session) => {
        this.persistSession(session, rememberSession);
        if (!session.changePassword) {
          this.credentialService.load(true).subscribe();
        }
      })
    );
  }

  forgotPassword(target: string, type: ForgotPasswordType = 'email'): Observable<AuthCodeTextResult> {
    return this.http.post<AuthCodeTextResult>(this.forgotPasswordEndpoint, {
      type,
      target,
    });
  }

  resetPassword(key: string, password: string): Observable<AuthCodeTextResult> {
    return this.http.post<AuthCodeTextResult>(this.resetPasswordEndpoint, { key, password });
  }

  changeCurrentPassword(oldPassword: string, newPassword: string): Observable<AuthCodeTextResult> {
    return this.http.post<AuthCodeTextResult>(this.resetCurrentPasswordEndpoint, { oldPassword, newPassword });
  }

  isPasswordChangeRequired(): boolean {
    return this.getToken() !== null && this.getSession()?.changePassword === true;
  }

  markPasswordChangeRequired(): void {
    const [localStore, sessionStore] = this.getStores();
    if (!localStore || !sessionStore) {
      return;
    }

    for (const store of [localStore, sessionStore]) {
      const session = this.readSession(store);
      if (session) {
        store.setItem(this.storageKey, JSON.stringify({ ...session, changePassword: true }));
      }
    }
  }

  endSession(): void {
    this.supportCacheResetService.clearAll();
    this.clearSession();
  }

  getToken(): string | null {
    const session = this.getSession();
    if (!session) {
      return null;
    }

    if (this.isExpired(session.expires)) {
      this.supportCacheResetService.clearAll();
      this.clearSession();
      return null;
    }

    return session.token;
  }

  getUser(): string | null {
    return this.getSession()?.user ?? null;
  }

  isAuthenticated(): boolean {
    return this.getToken() !== null;
  }

  logout(): void {
    this.supportCacheResetService.clearAll();
    this.clearSession();
    window.location.href = '/authentication/login';
  }

  private persistSession(session: SignInResponse, rememberSession: boolean): void {
    const [localStore, sessionStore] = this.getStores();
    if (!localStore || !sessionStore) {
      return;
    }

    const serializedSession = JSON.stringify(session);

    if (rememberSession) {
      sessionStore.removeItem(this.storageKey);
      localStore.setItem(this.storageKey, serializedSession);
      return;
    }

    localStore.removeItem(this.storageKey);
    sessionStore.setItem(this.storageKey, serializedSession);
  }

  private getSession(): SignInResponse | null {
    const [localStore, sessionStore] = this.getStores();
    if (!localStore || !sessionStore) {
      return null;
    }

    const localSession = this.readSession(localStore);
    if (localSession) {
      return localSession;
    }

    return this.readSession(sessionStore);
  }

  private readSession(store: Storage): SignInResponse | null {
    const serializedSession = store.getItem(this.storageKey);
    if (!serializedSession) {
      return null;
    }

    try {
      return JSON.parse(serializedSession) as SignInResponse;
    } catch {
      store.removeItem(this.storageKey);
      return null;
    }
  }

  private clearSession(): void {
    const [localStore, sessionStore] = this.getStores();
    localStore?.removeItem(this.storageKey);
    sessionStore?.removeItem(this.storageKey);
  }

  private getStores(): [Storage | null, Storage | null] {
    if (typeof window === 'undefined') {
      return [null, null];
    }

    return [window.localStorage, window.sessionStorage];
  }

  private isExpired(expires: string): boolean {
    const expiresAt = Date.parse(expires);
    return Number.isFinite(expiresAt) ? expiresAt <= Date.now() : false;
  }
}
